// @givmo/step-up — the one "Confirm it's you" sheet a step-up-refused act opens (AS-WEB3's payout
// dialog, lifted at CCM-R1-STEP-UP; drawn to D-AS-21's approved ConfirmSheet and given its passkey
// arm at AS-WEB10).
//
// WHAT IT OFFERS, in this order (frames B1a–B3d):
//   1. THE PASSKEY, first, when this prompt accepts one and the account holds one (the host's
//      `passkey` slot): "Use your passkey", the bound re-confirmation behind it. While the browser's
//      own prompt is up it waits (B3a); a dismissal or failure says "That didn't work. Try again,
//      or use your password instead." and stays (B3b), or "That didn't work. Try again." where the
//      passkey is the only way through (B3d). The person's own way in sits under it as a link
//      ("Use your password instead", "Use Google instead", which opens Google's window at once).
//   2. THE PERSON'S OWN WAY IN, when there is no passkey to use, or they chose it: their password
//      (no popup: the most reliable path, and the reason a host lists a linked password before a
//      linked provider), or their provider's own window. The first of the host's `waysIn` this
//      prompt accepts (`accepts`, named one by one), so a prompt that stops accepting the password
//      meets the same person with their provider. Its button reads "Continue" when an authenticator
//      code follows it (the host's `holdsAuthenticator`), else the row's confirm.
//   3. THE CODE, when the re-sign-in raised a second factor: the sheet's own six-box step (B1c)
//      when the host supplies `codeStep`, else the host's `renderSecondFactor`. Completing it IS the
//      completed confirmation.
// "Lost your passkey?" and "Lost your authenticator?" appear only where that factor is the only way
// through, and only when a host supplies where they lead. A host's `renderLost` (AS-WEB13: the
// account reset) opens INSIDE this sheet, as D-AS-21's B2d-lost draws it: its Back returns here, and
// closing it cancels the act like any dismissal. It is handed what the lost factor means for THIS
// act, in the row's own words (`lostLines`, AS-WEB18: Board's "You can still read and reply. …").
// Cancel (Back on the code step) sits at the foot beside the row's consequence. What the person
// typed in the form behind this sheet is never touched here.
//
// ⚠ While a challenge is pending the password form / provider button is HIDDEN (super-admin's fix,
// AS-WEB1): a second popup or a second password submit starts a NEW credential and orphans the
// outstanding challenge — the code the person is holding would then be rejected with no
// explanation. Back returns to the form; Escape still cancels, so the flow is never trapped.
//
// This sheet only ever RESOLVES the prompt; retrying the refused act is the gate's job
// (`use-step-up-gate.tsx`), which also refreshes the ID token first.

import { useCallback, useState, type ReactNode } from 'react';
import {
  Button,
  Dialog,
  ErrorMessage,
  Icon,
  OtpField,
  ProviderButton,
  TextField,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { Fingerprint } from 'lucide-react';
import type { StepUpWords } from './step-up-copy';
import {
  StepUpPasskeyFailure,
  acceptedStepUpWayIn,
  type StepUpHostAdapter,
  type StepUpLostFactor,
  type StepUpOwnWayIn,
  type StepUpWayIn,
} from './step-up-host';

export interface StepUpDialogProps {
  host: StepUpHostAdapter;
  words: StepUpWords;
  /** The ways in this prompt accepts (condition 1). */
  accepts: readonly StepUpWayIn[];
  /** The test-id prefix the host's surface keeps (payout's is `payment-step-up`). */
  testIdPrefix: string;
  /** The session is re-confirmed — the gate may retry the refused act. */
  onVerified: () => void;
  /** Dismissed without verifying. The gate fails the act with a `StepUpCancelledError`. */
  onCancel: () => void;
  /**
   * The prompt cannot be answered after all, for a typed reason the act should carry: a
   * passkey-only prompt whose passkey turned out not to exist (`StepUpPasskeyFailure`,
   * `none_registered`). Absent, that case reads as a cancel.
   */
  onFailed?: (reason: Error) => void;
}

const CODE_LENGTH = 6;

const LINK =
  'font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline disabled:opacity-60';
const QUIET_LINE = 'text-center font-sans text-s13 tracking-givmo text-text-tertiary';
const LOST_LINK =
  'self-center font-sans text-s12 font-w400 tracking-givmo text-text-tertiary underline underline-offset-2 outline-none hover:text-text-secondary focus-visible:text-text-secondary';

function TextButton({
  children,
  onClick,
  disabled,
  className,
  testId,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  className?: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(LINK, className)}
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

/** Which arm the sheet shows: the passkey, the person's own way in, or neither (an honest dead end). */
type Arm = 'passkey' | 'own' | 'none';

export function StepUpDialog({
  host,
  words,
  accepts,
  testIdPrefix: PREFIX,
  onVerified,
  onCancel,
  onFailed,
}: StepUpDialogProps) {
  const [password, setPassword] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [challenge, setChallenge] = useState<unknown>(null);
  /** The person picked an arm by its link; until then the sheet shows the default one. */
  const [chosen, setChosen] = useState<'passkey' | 'own' | null>(null);
  /** The passkey was dismissed or failed: say so above it. */
  const [passkeyFailed, setPasskeyFailed] = useState(false);
  /** The server said there is no passkey after all: stop offering one. */
  const [passkeyGone, setPasskeyGone] = useState(false);
  /** A lost link opened the host's view inside the sheet (`renderLost`). */
  const [lostView, setLostView] = useState<StepUpLostFactor | null>(null);

  const own = acceptedStepUpWayIn(host.waysIn, accepts);
  const slot = accepts.includes('passkey') && !passkeyGone ? host.passkey : undefined;
  const passkeyAvailable = slot !== undefined && slot.holdsPasskey;
  const defaultArm: Arm = passkeyAvailable ? 'passkey' : own !== undefined ? 'own' : 'none';
  const arm: Arm =
    chosen === 'passkey' && passkeyAvailable
      ? 'passkey'
      : chosen === 'own' && own !== undefined
        ? 'own'
        : defaultArm;
  // A re-sign-in on an account with an authenticator owes a code after it.
  const codeFollows = host.holdsAuthenticator === true;
  // The authenticator is the only way through when there is no passkey to use here.
  const renderLost = host.renderLost;
  const offersLostAuthenticator =
    (host.lostAuthenticator !== undefined || renderLost !== undefined) &&
    codeFollows &&
    !passkeyAvailable;
  const offersLostPasskey =
    (slot?.lost !== undefined || renderLost !== undefined) && passkeyAvailable && own === undefined;

  /** Run one re-authentication attempt, routing a raised second factor into the code step. */
  const attempt = useCallback(
    async (reauthenticate: () => Promise<void>) => {
      setError(null);
      setVerifying(true);
      try {
        await reauthenticate();
        onVerified();
      } catch (thrown) {
        // The credential was accepted and a second factor is now owed — a STATE of this
        // confirmation, not a failure of it. Without this branch an enrolled person could never
        // complete a gated act.
        const pending = host.secondFactorOf(thrown);
        if (pending !== null && pending !== undefined) {
          setChallenge(pending);
          setPassword('');
        } else {
          setError(host.describeError(thrown));
        }
      } finally {
        setVerifying(false);
      }
    },
    [host, onVerified],
  );

  const confirmWithPasskey = useCallback(async () => {
    if (slot === undefined) return;
    setError(null);
    setPasskeyFailed(false);
    setVerifying(true);
    try {
      await slot.confirm();
      onVerified();
    } catch (thrown) {
      if (thrown instanceof StepUpPasskeyFailure && thrown.reason === 'none_registered') {
        // No passkey after all: the person's own way in, or — where the passkey was the only way —
        // the act fails with the typed reason, so its caller can offer to set one up.
        setPasskeyGone(true);
        if (own === undefined) {
          if (onFailed !== undefined) onFailed(thrown);
          else onCancel();
        }
      } else if (thrown instanceof StepUpPasskeyFailure) {
        setPasskeyFailed(true);
      } else {
        setError(host.describeError(thrown));
      }
    } finally {
      setVerifying(false);
    }
  }, [host, slot, own, onVerified, onCancel, onFailed]);

  const cancel = useCallback(() => {
    // A verification in flight owns the sheet: dismissing mid-ceremony would settle the gate while
    // the confirmation is still resolving, and the retry would race the token refresh.
    if (verifying) return;
    onCancel();
  }, [verifying, onCancel]);

  /** The person's own way in, started from its link beside the passkey: a provider opens at once. */
  const switchToOwnWay = useCallback(
    (way: StepUpOwnWayIn) => {
      setChosen('own');
      setPasskeyFailed(false);
      setError(null);
      if (way.kind === 'provider') {
        void attempt(() => host.reauthenticateWithProvider(way.id));
      }
    },
    [attempt, host],
  );

  const sheetCodeStep = challenge !== null && host.codeStep !== undefined;
  const hostCodeStep = challenge !== null && host.codeStep === undefined;

  const lostPasskeyLink = offersLostPasskey && !verifying && (
    <button
      type="button"
      onClick={() => (renderLost !== undefined ? setLostView('passkey') : slot?.lost?.())}
      className={LOST_LINK}
      {...testIdProp(`${PREFIX}-lost-passkey`)}
    >
      {words.lostPasskey}
    </button>
  );
  const lostAuthenticatorLink = offersLostAuthenticator && !verifying && (
    <button
      type="button"
      onClick={() =>
        renderLost !== undefined ? setLostView('authenticator') : host.lostAuthenticator?.()
      }
      className={LOST_LINK}
      {...testIdProp(`${PREFIX}-lost-authenticator`)}
    >
      {words.lostAuthenticator}
    </button>
  );

  const description = sheetCodeStep
    ? words.codeLine
    : arm === 'own' && own !== undefined
      ? words.reSignInDescription({
          way: own.kind,
          ...(own.kind === 'provider' ? { providerLabel: own.label } : {}),
          codeFollows,
        })
      : words.description;

  // The host's lost view replaces the whole sheet, title included (it draws its own): Back returns to
  // the sheet exactly as it was, and closing it is the sheet's own cancel.
  const inLostView = lostView !== null && renderLost !== undefined;

  return (
    <Dialog.Root
      open
      onOpenChange={(next) => {
        if (!next) cancel();
      }}
    >
      <Dialog.Content
        aria-label={inLostView ? undefined : words.title}
        className="flex flex-col gap-16"
        testId={`${PREFIX}-dialog`}
      >
        {lostView !== null && renderLost !== undefined ? (
          renderLost({
            factor: lostView,
            onBack: () => setLostView(null),
            onClose: cancel,
            // What the lost factor means for THIS act, in its row's words (AS-WEB18).
            lines: words.lostLines(lostView),
          })
        ) : (
          <>
            {hostCodeStep ? null : (
              <>
                <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
                  {words.title}
                </Dialog.Title>
                <Dialog.Description className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary">
                  {description}
                </Dialog.Description>
              </>
            )}

            {sheetCodeStep && host.codeStep !== undefined ? (
              <CodeStep
                prefix={PREFIX}
                words={words}
                onSubmit={(code) => host.codeStep!.resolve(challenge, code)}
                isRejectedCode={host.codeStep.isRejectedCode}
                describeError={host.describeError}
                onResolved={onVerified}
                onBack={() => setChallenge(null)}
                lostLink={lostAuthenticatorLink}
              />
            ) : hostCodeStep ? (
              // A host that draws its own code step: it COMPLETES the same confirmation, so the gate can
              // retry straight from here. Its words say what the code authorises, never "finish
              // signing in": this person is already signed in.
              host.renderSecondFactor({
                challenge,
                title: words.challengeTitle,
                description: words.challengeLine,
                cancelLabel: words.back,
                onResolved: onVerified,
                onCancel: () => setChallenge(null),
              })
            ) : arm === 'passkey' ? (
              <div className="flex flex-col gap-10">
                {passkeyFailed && (
                  <ErrorMessage className="pt-0" testId={`${PREFIX}-passkey-failed`}>
                    {words.passkeyFailed(own ?? null)}
                  </ErrorMessage>
                )}
                {error !== null && (
                  <ErrorMessage className="pt-0" testId={`${PREFIX}-error`}>
                    {error}
                  </ErrorMessage>
                )}
                <Button
                  appearance="saas"
                  tone="primary"
                  size="lg"
                  fullWidth
                  loading={verifying}
                  leadingIcon={<Icon icon={Fingerprint} size={18} aria-hidden />}
                  onClick={() => void confirmWithPasskey()}
                  testId={`${PREFIX}-passkey`}
                >
                  {words.passkeyLabel}
                </Button>
                <p className={QUIET_LINE} {...testIdProp(`${PREFIX}-passkey-hint`)}>
                  {verifying ? words.passkeyWaiting : words.passkeyHint}
                </p>
                {!verifying && own !== undefined && (
                  <TextButton
                    className="self-center"
                    onClick={() => switchToOwnWay(own)}
                    testId={`${PREFIX}-use-instead`}
                  >
                    {words.useInstead(own)}
                  </TextButton>
                )}
                {lostPasskeyLink}
              </div>
            ) : arm === 'own' && own?.kind === 'password' ? (
              <div className="flex flex-col gap-14">
                <form
                  className="flex flex-col gap-14"
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (password.length === 0 || verifying) return;
                    void attempt(() => host.reauthenticateWithPassword(password));
                  }}
                  {...testIdProp(`${PREFIX}-form`)}
                >
                  <TextField
                    label={words.passwordLabel}
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    testId={`${PREFIX}-password`}
                  />
                  {error !== null && (
                    <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage>
                  )}
                  <Button
                    type="submit"
                    appearance="saas"
                    tone="primary"
                    size="lg"
                    fullWidth
                    loading={verifying}
                    disabled={password.length === 0}
                    testId={`${PREFIX}-verify`}
                  >
                    {codeFollows ? words.continueLabel : words.confirmLabel}
                  </Button>
                </form>
                {passkeyAvailable && !verifying && (
                  <TextButton
                    className="self-center"
                    onClick={() => {
                      setChosen('passkey');
                      setError(null);
                    }}
                    testId={`${PREFIX}-use-passkey`}
                  >
                    {words.usePasskeyInstead}
                  </TextButton>
                )}
                {lostAuthenticatorLink}
              </div>
            ) : arm === 'own' && own?.kind === 'provider' ? (
              <div className="flex flex-col gap-14">
                {error !== null && <ErrorMessage testId={`${PREFIX}-error`}>{error}</ErrorMessage>}
                {own.wayIn === 'google' || own.wayIn === 'microsoft' ? (
                  <ProviderButton
                    provider={own.wayIn}
                    label={words.providerLabel(own.label)}
                    loading={verifying}
                    onClick={() => void attempt(() => host.reauthenticateWithProvider(own.id))}
                    testId={`${PREFIX}-provider`}
                  />
                ) : (
                  <Button
                    appearance="saas"
                    tone="primary"
                    size="lg"
                    fullWidth
                    loading={verifying}
                    onClick={() => void attempt(() => host.reauthenticateWithProvider(own.id))}
                    testId={`${PREFIX}-provider`}
                  >
                    {words.providerLabel(own.label)}
                  </Button>
                )}
                {passkeyAvailable && !verifying && (
                  <TextButton
                    className="self-center"
                    onClick={() => {
                      setChosen('passkey');
                      setError(null);
                    }}
                    testId={`${PREFIX}-use-passkey`}
                  >
                    {words.usePasskeyInstead}
                  </TextButton>
                )}
                {lostAuthenticatorLink}
              </div>
            ) : (
              // No way in this prompt can run here. Say so plainly rather than render a prompt that
              // cannot succeed. A prompt only a passkey answers is never told to sign in again: a
              // password or provider sign-in does not answer it, and what failed is reading the
              // account's passkeys here.
              <ErrorMessage testId={`${PREFIX}-unavailable`}>
                {accepts.length > 0 && accepts.every((way) => way === 'passkey')
                  ? words.passkeyUnavailable
                  : words.unavailable}
              </ErrorMessage>
            )}

            {sheetCodeStep ? null : (
              <div className="flex flex-wrap items-center justify-between gap-8">
                <TextButton disabled={verifying} onClick={cancel} testId={`${PREFIX}-cancel`}>
                  {words.cancel}
                </TextButton>
                {words.consequence !== undefined && (
                  <span
                    className="font-sans text-s12 tracking-givmo text-text-tertiary"
                    {...testIdProp(`${PREFIX}-consequence`)}
                  >
                    {words.consequence}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}

/**
 * The sheet's own code step (B1c, B2d-fail): six boxes, the row's final confirm, Back to the
 * credential step, and the consequence at the foot like every other state. A refused code clears the
 * boxes and says so; anything else shows the host's words for it.
 */
function CodeStep({
  prefix,
  words: stepWords,
  onSubmit,
  isRejectedCode,
  describeError,
  onResolved,
  onBack,
  lostLink,
}: {
  prefix: string;
  words: StepUpWords;
  onSubmit: (code: string) => Promise<void>;
  isRejectedCode: (thrown: unknown) => boolean;
  describeError: (thrown: unknown) => string | null;
  onResolved: () => void;
  onBack: () => void;
  lostLink: ReactNode;
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [rejected, setRejected] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  return (
    <>
      <form
        className="flex flex-col gap-14"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (code.length !== CODE_LENGTH || submitting) return;
          setSubmitting(true);
          setRejected(false);
          setStepError(null);
          void onSubmit(code)
            .then(onResolved, (thrown: unknown) => {
              // A code is single-use: clear it either way, so the next try is a fresh one.
              setCode('');
              if (isRejectedCode(thrown)) setRejected(true);
              else setStepError(describeError(thrown));
            })
            .finally(() => setSubmitting(false));
        }}
        {...testIdProp(`${prefix}-code-form`)}
      >
        <div className="flex flex-col">
          <OtpField
            length={CODE_LENGTH}
            value={code}
            onValueChange={(next) => {
              setCode(next);
              if (next.length > 0) setRejected(false);
            }}
            error={rejected}
            aria-label={stepWords.codeLabel}
            testId={`${prefix}-code`}
          />
          {rejected && (
            <ErrorMessage testId={`${prefix}-code-rejected`}>{stepWords.codeRejected}</ErrorMessage>
          )}
          {stepError !== null && (
            <ErrorMessage testId={`${prefix}-error`}>{stepError}</ErrorMessage>
          )}
        </div>
        <Button
          type="submit"
          appearance="saas"
          tone="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={code.length !== CODE_LENGTH}
          testId={`${prefix}-code-confirm`}
        >
          {stepWords.confirmLabel}
        </Button>
        {lostLink}
      </form>
      <div className="flex flex-wrap items-center justify-between gap-8">
        <TextButton disabled={submitting} onClick={onBack} testId={`${prefix}-back`}>
          {stepWords.back}
        </TextButton>
        {stepWords.consequence !== undefined && (
          <span
            className="font-sans text-s12 tracking-givmo text-text-tertiary"
            {...testIdProp(`${prefix}-consequence`)}
          >
            {stepWords.consequence}
          </span>
        )}
      </div>
    </>
  );
}
